import { TABLET_ACCESS_KEY } from '../../config/adminPages';

/**
 * The staff form's role list (Jason, 23 Oct 2026): Admin, Staff, Staff - Vaperite, Tablet.
 * Owner exists (Jason) but is not offered. Each choice is a backend role plus the access it
 * starts with: the ticks it pre-fills (still editable), and which stores it can be given.
 * null access = the role default (everything the role allows). "Staff" and "Staff - Vaperite"
 * are the same backend role; the label only says which ticks and stores it started with.
 */
export interface RoleChoice {
  value: string;
  label: string;
  role: string;
  description: string;
  pagePermissions: string[] | null;
  requiresStores: boolean;
  /** Offer only stores of this Lightspeed account. */
  storeOrg?: 'vaperite';
}

export const ROLE_CHOICES: RoleChoice[] = [
  { value: 'admin', label: 'Admin', role: 'admin', description: 'Full access to all locations and settings', pagePermissions: null, requiresStores: false },
  {
    value: 'staff', label: 'Staff', role: 'staff',
    description: 'Assigned stores only; tick the pages they may use',
    pagePermissions: null, requiresStores: true,
  },
  {
    value: 'staff-vaperite', label: 'Staff – Vaperite', role: 'staff',
    description: 'The Vaperite section and Orders, for Vaperite stores only',
    pagePermissions: ['vaperite', 'orders.list'], requiresStores: true, storeOrg: 'vaperite',
  },
  {
    value: 'tablet', label: 'Tablet', role: 'tablet',
    description: 'The order tablet only, for assigned stores — no admin access',
    pagePermissions: [TABLET_ACCESS_KEY], requiresStores: true,
  },
];

/** The staff roles the admin knows, highest first. */
export const ADMIN_STAFF_ROLES = ['owner', 'admin', 'staff'] as const;

export const choiceFor = (value: string): RoleChoice =>
  ROLE_CHOICES.find((c) => c.value === value) ?? ROLE_CHOICES.find((c) => c.value === 'staff')!;

const sameSet = (a: string[] | null, b: string[] | null) =>
  !!a && !!b && a.length === b.length && a.every((k) => b.includes(k));

/** The choice an existing user reads back as: a preset when role and ticks match it. */
export function choiceForUser(roles: string[], pagePermissions: string[] | null): RoleChoice {
  if (roles.includes('tablet') && !roles.some((r) => (ADMIN_STAFF_ROLES as readonly string[]).includes(r))) {
    return choiceFor('tablet');
  }
  const role = ['admin', 'staff'].find((r) => roles.includes(r)) ?? 'staff';
  const preset = ROLE_CHOICES.find((c) => c.role === role && c.pagePermissions && sameSet(c.pagePermissions, pagePermissions));
  return preset ?? choiceFor(role);
}

/** The stores a choice may be given. */
export function storesForChoice<T extends { vendOrg?: string }>(choice: RoleChoice, stores: T[]): T[] {
  return choice.storeOrg ? stores.filter((s) => s.vendOrg === choice.storeOrg) : stores;
}

export interface StoreAccess {
  /** green = every store; orange = a subset */
  level: 'all' | 'some';
  label: string;
}

/**
 * The Assigned Stores chip on the staff list. No rows = an all-stores role (Admin):
 * every store, including ones added later. Rows covering every current store
 * read as All stores too, and drop to "11/12" the day a store is added, so a gap shows.
 */
export function storeAccess(assigned: { id: string; name: string }[], allStoreIds: string[]): StoreAccess {
  if (assigned.length === 0) return { level: 'all', label: 'All stores' };
  const mine = new Set(assigned.map((s) => s.id));
  if (allStoreIds.length > 0 && allStoreIds.every((id) => mine.has(id))) return { level: 'all', label: 'All stores' };
  if (assigned.length === 1) return { level: 'some', label: `1 Store: ${assigned[0].name}` };
  return { level: 'some', label: allStoreIds.length ? `${assigned.length}/${allStoreIds.length}` : `${assigned.length} stores` };
}

/** The role chip on the staff list: the form's name for it. Owner is not a form choice. */
export const roleLabel = (roles: string[], pagePermissions: string[] | null): string =>
  roles.includes('owner') ? 'Owner' : choiceForUser(roles, pagePermissions).label;
